import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { PowerCalendarDay } from 'ember-power-calendar/utils';
import GeneralSmartInsightsRecommendationSummary from 'lumen/components/general/smart-insights/recommendation-summary';
import type { SmartInsightDay } from 'lumen/models';
import { SMART_INSIGHT_PRIORITIES } from 'lumen/models/smart-insights/smart-insight-day';

import styles from './day-smart-insights.module.css';

interface GeneralSmartInsightsCalendarDaySmartInsightsSignature {
  Args: {
    day: PowerCalendarDay;
    dayClass: string;
    insight: SmartInsightDay | null;
    selectSmartInsightsDay: (day: string | null) => void;
    tooltipHoverTrackingTask: TaskForAsyncTaskFunction<
      {
        restartable: boolean;
      },
      (insight: SmartInsightDay) => Promise<void>
    >;
  };
}

export default class GeneralSmartInsightsCalendarDaySmartInsights extends Component<GeneralSmartInsightsCalendarDaySmartInsightsSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare smartInsights: Services['smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get classNameByPriority(): string {
    const { insight } = this.args;

    if (!insight) {
      return '';
    }

    switch (insight.stayDatePriority) {
      case SMART_INSIGHT_PRIORITIES.HIGH:
        return styles['day-high-priority'];
      case SMART_INSIGHT_PRIORITIES.MEDIUM:
        return styles['day-medium-priority'];
      case SMART_INSIGHT_PRIORITIES.LOW:
        return styles['day-low-priority'];
      default:
        return '';
    }
  }

  get dayNumberAsString(): string {
    const { day } = this.args;

    return `${day.number}`;
  }

  get isDisabled(): boolean {
    const minDate = this.hotelAndMarketService.getLocalTime().startOf('day');
    const maxDate = minDate
      .add(this.smartInsights.leadtime, 'day')
      .startOf('day');

    const { day } = this.args;
    const dayDate = this.hotelAndMarketService
      .toLocalTime(day.date.toDateString())
      .startOf('day');

    return dayDate.isBefore(minDate) || dayDate.isAfter(maxDate);
  }

  get showInsight(): boolean {
    const { insight } = this.args;

    // We currently fetch 120 days of insights, but only show 90 days in the calendar. This is to avoid
    // re-recording all the tests with the updated QP. This is a temporary fix to make sure we don't show insights on disabled days.
    if (!insight || this.isDisabled) {
      return false;
    }
    // KPI-only days (no text entries) are always shown. For days with text entries,
    // only show if at least one entry renders successfully — hides days where every
    // template failed (avoids showing a confusing empty-insight state).
    const hasTextEntries = insight.insightEntries.length > 0;
    return !hasTextEntries || insight.hasRenderableInsightEntries;
  }

  get showSelectableDay(): boolean {
    return !this.isDisabled && !this.args.insight;
  }

  get tooltipInsightsCount(): number {
    const { insight } = this.args;

    if (!insight) {
      return 0;
    }

    return Math.max(0, insight.insightEntriesSortedByRelevance.length - 1);
  }

  get tooltipText(): string | null {
    const { insight } = this.args;

    if (!insight) {
      return '';
    }

    const { insightEntriesSortedByRelevance } = insight;

    return insightEntriesSortedByRelevance[0]?.formattedText ?? null;
  }

  @action onClickDay(): void {
    const { selectSmartInsightsDay, insight, day } = this.args;

    const dayFormatted = dayjs(day.date).format('YYYY-MM-DD');

    selectSmartInsightsDay(dayFormatted);

    this.usageAnalytics.track('Smart Insights Detail Viewed', {
      eventTrigger: 'smart-insights-calendar',
      product: 'smart-insights',
      ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
        insight,
        dayFormatted,
      ),
    });
  }

  @action onMouseLeave(): void {
    const { tooltipHoverTrackingTask } = this.args;

    if (tooltipHoverTrackingTask.isRunning) {
      void tooltipHoverTrackingTask.cancelAll();
    }
  }

  @action onShowTooltip(): void {
    const { insight, tooltipHoverTrackingTask } = this.args;

    if (!insight) {
      return;
    }

    void tooltipHoverTrackingTask.perform(insight);
  }

  <template>
    {{#if this.showInsight}}
      <PrismButton
        @buttonType="ghost"
        @label={{this.dayNumberAsString}}
        class="smart-insights-calendar-day relative
          {{@dayClass}}
          {{this.classNameByPriority}}"
        data-test-id="smart-insights-calendar-day-with-insights"
        {{on "click" this.onClickDay}}
        {{on "mouseleave" this.onMouseLeave}}
      >
        {{#if @insight.hasRecommendations}}
          <div
            class={{styles.recommendation-badge}}
            data-test-id="smart-insights-calendar-day-recommendation-badge"
          >
            <PrismIcon
              @color="intelligence"
              @name="recommendation"
              @size="extra-small"
            />
          </div>
        {{/if}}

        {{#if this.tooltipText}}
          <EmberTooltip
            @onShow={{this.onShowTooltip}}
            @side="top"
            @tooltipClass="rounded-200 p-300 bg-elevation-surface text-300--regular text-neutral-default shadow-neutral-200 tooltip-calendar-smart-insights"
          >
            <div class="flex flex-col gap-100">
              <p>
                {{htmlSafe this.tooltipText}}
              </p>

              {{#if this.tooltipInsightsCount}}
                <p class="text-200--regular text-neutral-subdued">
                  {{t
                    "smart_insights.calendar.tooltip.more_insights"
                    count=this.tooltipInsightsCount
                  }}
                </p>
              {{/if}}

              {{#if @insight.recommendationSummaryLabel}}
                <div class="border-neutral-default border-t pt-100">
                  <GeneralSmartInsightsRecommendationSummary
                    @label={{@insight.recommendationSummaryLabel}}
                  />
                </div>
              {{/if}}
            </div>
          </EmberTooltip>
        {{/if}}
      </PrismButton>
    {{else if this.showSelectableDay}}
      <PrismButton
        @buttonType="secondary"
        @label={{this.dayNumberAsString}}
        class="smart-insights-calendar-day
          {{@dayClass}}
          {{styles.day-no-insight}}"
        data-test-id="smart-insights-calendar-day-without-insights"
        {{on "click" this.onClickDay}}
      />
    {{else}}
      <div
        class="text-200--regular flex items-center justify-center gap-100
          {{@dayClass}}
          {{if this.isDisabled 'ember-power-calendar-day--disabled'}}"
      >
        {{this.dayNumberAsString}}
      </div>
    {{/if}}
  </template>
}
